/* eslint-disable camelcase */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as HighMaps from 'highcharts/highmaps';
import HighchartsReact from 'highcharts-react-official';
import worldMap from '@highcharts/map-collection/custom/world.geo.json';
import JsonQuery from 'json-query';
import parse from 'parse-json';
import { merge } from 'object-path-immutable';
import _ from 'lodash';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getColorPalette,
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import {
    hongKongFeature,
    bermudaFeature,
    singaporeFeature,
    koreaFeature,
    chinaCords,
} from './extraMapRegions';

interface HighMapOption {
    path: string;
    value: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    mapTitles?: Headings[];
    dataKey: string;
    options?: HighMapOption[];
    countryNameKey: string;
    countryValueKey: string;
    countryCodeKey: string;
    colors: string;
    dataLimit?: number;
    typographyClass: string;
    disableTabStaticData?: boolean;
    showOtherRegions?: boolean;
}

const GeographicAllocationMap = (props: commonTypes.WidgetInstanceProps) => {
    const { documentType } = commonHooks.useAppContext();
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );

    const { data } = props;
    const { styles, mapTitles, dataKey, options, ...rest } = widgetProperties || props;

    const [isMounted, setIsMounted] = useState<boolean>(false);
    const [renderChart, setRenderChart] = useState<boolean>(false);

    const mapData: any = JsonQuery(`${dataKey}`, { data }).value;

    let colors = parse((rest.colors as string) || '{}');

    // eslint-disable-next-line
    const customColorPallete: string[] = (data as any)?.colorPallete || [];

    const i18n = commonHooks.useLocalization();

    if (customColorPallete.length > 0) {
        colors = customColorPallete;
    }

    if (Object.keys(colors).length < 1 || colors.length < 1) {
        colors = getColorPalette();
    }

    const seriesData: any = _.cloneDeep(
        mapData?.length &&
            mapData.slice(0, rest.dataLimit || 10).map((m: any, i: number) => ({
                'hc-key': _.get(m, rest.countryCodeKey),
                name: m[rest.countryNameKey],
                value: m[rest.countryValueKey],
                color: colors[i],
            }))
    );

    // Clone the world map GeoJSON
    const updatedWorldMap = { ...worldMap };

    useEffect(() => {
        if (rest?.showOtherRegions) {
            // check if seriesData has Hong Kong in it
            const hasHongKong = seriesData?.findIndex((feature: any) => feature['hc-key'] === 'hk');

            if (hasHongKong !== -1) {
                const chinaIndex = updatedWorldMap?.features?.findIndex(
                    (feature: any) => feature.id === 'CN'
                );
                // Update manual chinese map coordionates
                updatedWorldMap.features[chinaIndex].geometry.coordinates[1] = chinaCords;
            }

            updatedWorldMap.features.push(
                hongKongFeature,
                singaporeFeature,
                bermudaFeature,
                koreaFeature
            );
        }

        setIsMounted(true);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (seriesData && seriesData.length > 0 && !renderChart) {
            setRenderChart(true);
        }
    }, [seriesData, renderChart]);

    let highMapsOptions: HighMaps.Options = {
        chart: {
            backgroundColor: 'transparent',
            type: 'map',
            map: 'custom/world',
        },
        xAxis: { visible: false },
        yAxis: { visible: false },
        title: { text: '' },
        credits: { enabled: false },
        colors,
        colorAxis: {
            dataClasses: [],
        },
        legend: { enabled: false },
        tooltip: {
            enabled: true,
            formatter: function () {
                const value: any = this.point?.value;
                return `
                    <div>
                        <span style="color:${this.point?.color}">\u25CF</span>
                        ${this.point?.options?.name}: ${i18n.t(value, 'pctMax1')}
                    </div>
                `;
            },
        },
        series: [
            {
                type: 'map',
                mapData: updatedWorldMap,
                cursor: 'pointer',
                joinBy: ['hc-key', 'hc-key'],
                data: seriesData,
            },
        ],
    };

    Array.isArray(options) &&
        options.forEach((o: HighMapOption) => {
            let userOption;

            try {
                userOption = parse((o.value as string) || '{}');
            } catch (error) {
                userOption = {};
            }

            if (o.path === 'series') {
                highMapsOptions = {
                    ...highMapsOptions,
                    series: [{ ...highMapsOptions?.series?.[0], ...userOption }],
                };
            } else if (o.path === 'colors') {
                highMapsOptions = { ...highMapsOptions, colors: userOption };
            } else {
                highMapsOptions = merge(highMapsOptions, o.path, userOption);
            }
        });

    const mapId = uuidv4();

    return (
        <>
            {isMounted && (
                <div
                    className={`map ${
                        widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                    }`}
                    css={css`
                        ${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                >
                    <HeaderWrapper
                        headings={mapTitles}
                        data={data}
                        disableTabStaticData={
                            globalDisableTabStaticData || rest.disableTabStaticData
                        }
                    />
                    <div id={`${mapId}`} className="js-map-tile">
                        {['pdf', 'pptx'].includes(documentType) ? (
                            <div id={`${mapId}-map-chart`} />
                        ) : (
                            <>
                                {renderChart && (
                                    <HighchartsReact
                                        highcharts={HighMaps}
                                        constructorType={'mapChart'}
                                        options={highMapsOptions}
                                        allowChartUpdate={true}
                                        updateArgs={[true, true]}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            {['pdf', 'pptx'].includes(documentType) && (
                <>
                    <script
                        id={`${mapId}-chart-options`}
                        type="text/json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(highMapsOptions) }}
                    ></script>
                    <script
                        id={`${mapId}-json-data`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify({ updatedWorldMap, seriesData }),
                        }}
                    ></script>
                </>
            )}
        </>
    );
};

export default GeographicAllocationMap;
